/** @jsxImportSource @emotion/react */
import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react'
import { css } from '@emotion/react'
import { Tour } from 'antd'
import type { TourStepProps } from 'antd'
import colors from '@sportninja/common/constants/appColors'

interface ConstraintsTutorialProps {
  open: boolean
  onClose: () => void
}

const panelStyles = css`
  background-color: ${colors.SECONDARY_400};
  border: 1px solid ${colors.SECONDARY_200};
  border-radius: 12px;
  padding: 20px;
  max-width: 340px;
  font-family: 'Inter', sans-serif;
`

const titleStyles = css`
  color: ${colors.NEUTRAL_0};
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 8px 0;
`

const descStyles = css`
  color: ${colors.NEUTRAL_50};
  font-size: 13px;
  line-height: 1.5;
  margin: 0 0 16px 0;
`

const dotsContainerStyles = css`
  display: flex;
  gap: 6px;
  margin-bottom: 16px;
`

const buttonRowStyles = css`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
`

const skipBtnStyles = css`
  background: transparent;
  border: none;
  color: ${colors.NEUTRAL_100};
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  cursor: pointer;
  padding: 6px 12px;
  margin-right: auto;

  &:hover {
    color: ${colors.NEUTRAL_50};
  }
`

const backBtnStyles = css`
  background: transparent;
  border: 1px solid ${colors.NEUTRAL_500};
  color: ${colors.NEUTRAL_50};
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  padding: 6px 16px;
  border-radius: 6px;

  &:hover {
    border-color: ${colors.NEUTRAL_100};
    color: ${colors.NEUTRAL_0};
  }
`

const nextBtnStyles = css`
  background-color: ${colors.PRIMARY};
  border: none;
  color: ${colors.PRIMARY_TEXT};
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 6px 16px;
  border-radius: 6px;

  &:hover {
    background-color: ${colors.PRIMARY_HOVER};
  }
`

const TOTAL_STEPS = 4

const STEP_CONTENT: { title: string; description: string }[] = [
  {
    title: 'Welcome to Constraints',
    description:
      'Constraints are rules that control how the scheduler generates your games. Enable and configure them to fit your league\u2019s needs.',
  },
  {
    title: 'Enable a Constraint',
    description:
      'Toggle the switch to activate a constraint. Once enabled, it will be applied when generating the schedule.',
  },
  {
    title: 'Hard vs Soft',
    description:
      'Hard constraints must be met \u2014 the schedule won\u2019t generate if violated. Soft constraints are preferences the scheduler tries to satisfy.',
  },
  {
    title: 'Configure Parameters',
    description:
      'Click the pencil icon to customize settings like dates, teams, or values for each constraint.',
  },
]

const queryTarget = (selector: string): (() => HTMLElement) => {
  return () => {
    const el = document.querySelector(selector)
    return (el as HTMLElement) || document.body
  }
}

interface TourPanelProps {
  stepIndex: number
  onSkip: () => void
  onBack: () => void
  onNext: () => void
}

const TourPanel: React.FC<TourPanelProps> = ({
  stepIndex,
  onSkip,
  onBack,
  onNext,
}) => {
  const content = STEP_CONTENT[stepIndex]
  const isLast = stepIndex === TOTAL_STEPS - 1
  const isFirst = stepIndex === 0

  return (
    <div css={panelStyles}>
      <h3 css={titleStyles}>{content.title}</h3>
      <p css={descStyles}>{content.description}</p>

      <div css={dotsContainerStyles}>
        {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
          <span
            key={i}
            css={css`
              width: 8px;
              height: 8px;
              border-radius: 50%;
              background-color: ${i === stepIndex
                ? colors.PRIMARY
                : colors.NEUTRAL_500};
              transition: background-color 0.2s ease;
            `}
          />
        ))}
      </div>

      <div css={buttonRowStyles}>
        {!isLast && (
          <button type='button' css={skipBtnStyles} onClick={onSkip}>
            Skip
          </button>
        )}
        {!isFirst && (
          <button type='button' css={backBtnStyles} onClick={onBack}>
            Back
          </button>
        )}
        <button type='button' css={nextBtnStyles} onClick={onNext}>
          {isLast ? 'Got it' : 'Next'}
        </button>
      </div>
    </div>
  )
}

const ConstraintsTutorial: React.FC<ConstraintsTutorialProps> = ({
  open,
  onClose,
}) => {
  const [current, setCurrent] = useState(0)
  const centerAnchorRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (open) {
      setCurrent(0)
    }
  }, [open])

  const handleNext = useCallback(() => {
    if (current < TOTAL_STEPS - 1) {
      setCurrent((prev) => prev + 1)
    } else {
      onClose()
    }
  }, [current, onClose])

  const handleBack = useCallback(() => {
    if (current > 0) {
      setCurrent((prev) => prev - 1)
    }
  }, [current])

  const steps: TourStepProps[] = useMemo(
    () => [
      {
        title: '',
        description: '',
        target: () => centerAnchorRef.current as HTMLElement,
        placement: 'bottom' as const,
        renderPanel: () => (
          <TourPanel
            stepIndex={0}
            onSkip={onClose}
            onBack={handleBack}
            onNext={handleNext}
          />
        ),
      },
      {
        title: '',
        description: '',
        target: queryTarget('[data-tour-switch]'),
        placement: 'bottom' as const,
        renderPanel: () => (
          <TourPanel
            stepIndex={1}
            onSkip={onClose}
            onBack={handleBack}
            onNext={handleNext}
          />
        ),
      },
      {
        title: '',
        description: '',
        target: queryTarget('[data-tour-type-badge]'),
        placement: 'bottom' as const,
        renderPanel: () => (
          <TourPanel
            stepIndex={2}
            onSkip={onClose}
            onBack={handleBack}
            onNext={handleNext}
          />
        ),
      },
      {
        title: '',
        description: '',
        target: queryTarget('[data-tour-edit]'),
        placement: 'bottom' as const,
        renderPanel: () => (
          <TourPanel
            stepIndex={3}
            onSkip={onClose}
            onBack={handleBack}
            onNext={handleNext}
          />
        ),
      },
    ],
    [onClose, handleBack, handleNext]
  )

  return (
    <>
      <div
        ref={centerAnchorRef}
        css={css`
          position: fixed;
          top: 20%;
          left: 50%;
          width: 1px;
          height: 1px;
          pointer-events: none;
          z-index: -1;
        `}
      />
      <Tour
        open={open}
        current={current}
        onChange={setCurrent}
        onClose={onClose}
        steps={steps}
        mask={{ color: 'rgba(0, 0, 0, 0.65)' }}
        gap={{ offset: 8, radius: 8 }}
        animated
        zIndex={1050}
      />
    </>
  )
}

export default ConstraintsTutorial
